import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { App } from "./App.js";
import { parseEnv } from "./config/env.js";
import { applyTheme, resolveInitialTheme, watchSystemTheme } from "./styles/theme.js";
import "./styles/global.css";

const env = parseEnv(import.meta.env);

/**
 * Dev-only: derive the API origin from the page's own host when the
 * configured `VITE_API_BASE_URL` points at `localhost`. Lets the
 * console reach the API at the same host it was loaded from (e.g. a
 * LAN IP) without flipping the env per device. Production deploys set
 * an explicit absolute URL, so this branch never fires there.
 */
const apiBaseUrl = (() => {
  if (env.mode === "production") return env.apiBaseUrl;
  if (typeof window === "undefined") return env.apiBaseUrl;
  try {
    const configured = new URL(env.apiBaseUrl);
    const isLocalhost =
      configured.hostname === "localhost" || configured.hostname === "127.0.0.1";
    if (!isLocalhost) return env.apiBaseUrl;
    const pageHost = window.location.hostname;
    if (pageHost === "localhost" || pageHost === "127.0.0.1") {
      return env.apiBaseUrl;
    }
    return `${configured.protocol}//${pageHost}:${configured.port || "8080"}`;
  } catch {
    return env.apiBaseUrl;
  }
})();

const resolvedEnv = { ...env, apiBaseUrl };

applyTheme(resolveInitialTheme());
watchSystemTheme(applyTheme);

const container = document.getElementById("root");
if (container === null) {
  throw new Error(
    "Support console root container <div id=\"root\"> not found in index.html."
  );
}

createRoot(container).render(
  <StrictMode>
    <App env={resolvedEnv} />
  </StrictMode>
);
